import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Clock, Loader2, Wrench } from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import { serviceJobsApi } from '@/services/api';
import type { POSServiceJob } from '@/types';
import { formatMoney } from '@/utils/currency';

type Tab = 'incomplete' | 'upcoming' | 'completed';

export default function MyServicesPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('incomplete');

  const { data: jobs = [], isLoading } = useQuery({
    queryKey: ['myServiceJobs'],
    queryFn: async () => {
      const res = await serviceJobsApi.listJobs({ mine: 1, limit: 200 });
      return (res.data.message || []) as POSServiceJob[];
    },
    refetchInterval: 60_000,
  });

  const { data: commission } = useQuery({
    queryKey: ['myServiceCommission'],
    queryFn: async () => {
      const res = await serviceJobsApi.myCommission();
      return res.data.message as { earned: number; paid: number; unpaid: number };
    },
  });

  const completeMut = useMutation({
    mutationFn: (name: string) => serviceJobsApi.updateStatus(name, 'Completed'),
    onSuccess: () => {
      toast.success(t('services.markedComplete', 'Marked complete'));
      void qc.invalidateQueries({ queryKey: ['myServiceJobs'] });
      void qc.invalidateQueries({ queryKey: ['myServiceCommission'] });
    },
    onError: () => toast.error(t('common.error', 'Something went wrong')),
  });

  const startMut = useMutation({
    mutationFn: (name: string) => serviceJobsApi.updateStatus(name, 'In Progress'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['myServiceJobs'] });
    },
  });

  const filtered = useMemo(() => {
    const now = Date.now();
    if (tab === 'incomplete') {
      return jobs.filter((j) => j.status === 'Pending' || j.status === 'In Progress');
    }
    if (tab === 'upcoming') {
      return jobs.filter(
        (j) =>
          (j.status === 'Pending' || j.status === 'In Progress') &&
          j.scheduled_at &&
          new Date(j.scheduled_at).getTime() > now,
      );
    }
    return jobs.filter((j) => j.status === 'Completed');
  }, [jobs, tab]);

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <TrialBanner />
        <div className="p-4 sm:p-6 border-b border-gray-200 bg-white">
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Wrench className="w-5 h-5 text-primary-600" />
            {t('services.myServices', 'My Services')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('services.myServicesSub', 'Your assigned jobs and commission')}
          </p>
          <div className="grid grid-cols-3 gap-2 mt-4">
            <div className="rounded-xl bg-primary-50 border border-primary-100 p-3">
              <p className="text-[11px] text-primary-700 font-medium">{t('services.earned', 'Earned')}</p>
              <p className="text-base font-bold text-primary-900">{formatMoney(commission?.earned || 0)}</p>
            </div>
            <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3">
              <p className="text-[11px] text-emerald-700 font-medium">{t('services.paid', 'Paid')}</p>
              <p className="text-base font-bold text-emerald-900">{formatMoney(commission?.paid || 0)}</p>
            </div>
            <div className="rounded-xl bg-amber-50 border border-amber-100 p-3">
              <p className="text-[11px] text-amber-700 font-medium">{t('services.unpaid', 'Unpaid')}</p>
              <p className="text-base font-bold text-amber-900">{formatMoney(commission?.unpaid || 0)}</p>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            {([
              ['incomplete', t('services.incomplete', 'Incomplete')],
              ['upcoming', t('services.upcoming', 'Upcoming')],
              ['completed', t('services.completed', 'Completed')],
            ] as const).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  tab === key ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3">
          {isLoading && (
            <div className="flex justify-center py-12">
              <Loader2 className="w-7 h-7 animate-spin text-primary-600" />
            </div>
          )}
          {!isLoading && filtered.length === 0 && (
            <p className="text-center text-gray-400 py-12">{t('services.noJobs', 'No jobs here')}</p>
          )}
          {filtered.map((job) => (
            <div key={job.name} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{job.item_name}</p>
                  <p className="text-sm text-gray-500">{job.customer_name || '—'}</p>
                  <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {job.scheduled_at
                      ? new Date(job.scheduled_at).toLocaleString()
                      : t('services.walkIn', 'Walk-in / Serve now')}
                  </p>
                  <p className="text-sm font-semibold text-primary-700 mt-1">{formatMoney(job.amount || 0)}</p>
                </div>
                <span
                  className={`shrink-0 text-xs font-medium px-2 py-1 rounded-full ${
                    job.status === 'Completed'
                      ? 'bg-emerald-50 text-emerald-700'
                      : job.status === 'In Progress'
                        ? 'bg-blue-50 text-blue-700'
                        : 'bg-amber-50 text-amber-700'
                  }`}
                >
                  {job.status}
                </span>
              </div>
              {(job.status === 'Pending' || job.status === 'In Progress') && (
                <div className="flex gap-2 mt-3">
                  {job.status === 'Pending' && (
                    <button
                      type="button"
                      onClick={() => startMut.mutate(job.name)}
                      className="flex-1 btn-secondary text-sm py-2"
                    >
                      {t('services.start', 'Start')}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => completeMut.mutate(job.name)}
                    disabled={completeMut.isPending}
                    className="flex-1 btn-primary text-sm py-2 inline-flex items-center justify-center gap-1"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    {t('services.complete', 'Complete')}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
